import { EditorContent, type FocusPosition, type JSONContent, useEditor } from '@tiptap/react';
import { type Ref, useImperativeHandle } from 'react';

import { clsx } from '~/styles/utils';

import type { EditorRefHandle, Shortcode } from './RichTextEditor.types';

import { controlThemeStyle, focusRingStyle } from '../Form/form.css';
import { extensions } from './extensions';
import { LinkPopover } from './LinkPopover';
import { containerStyle, editorStyle } from './RichTextEditor.css';
import { Toolbar } from './Toolbar';

type Props = {
  autofocus?: FocusPosition;
  /** Forces the focused appearance, e.g. to showcase it in the design system. */
  'data-focused'?: boolean;
  isInvalid: boolean;
  onBlur?: () => void;
  ref?: Ref<EditorRefHandle>;
  /** Codes the backend substitutes when it sends the content, offered from the toolbar. */
  shortcodes?: Shortcode[];
  withLinkButton?: boolean;
} & (
  | {
      mode: 'json';
      onChange?: (content: JSONContent) => void;
      value: JSONContent;
    }
  | {
      mode: 'html';
      onChange?: (content: string) => void;
      value: string;
    }
);

export const RichTextEditor = ({
  autofocus = false,
  'data-focused': isFocused,
  isInvalid,
  mode,
  onBlur,
  onChange,
  ref,
  shortcodes,
  value,
  withLinkButton = true,
}: Props) => {
  const editor = useEditor({
    editorProps: { attributes: { class: editorStyle } },
    extensions: extensions('editor'),
    content: value,
    onUpdate: ({ editor }) => (mode === 'json' ? onChange?.(editor.getJSON()) : onChange?.(editor.isEmpty ? '' : editor.getHTML())),
    onBlur: () => onBlur?.(),
    autofocus,
  });

  // Expose editor methods to the parent component.
  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor.commands.focus(),
      setContent: (content) => editor.commands.setContent(content),
      isEmpty: () => editor.isEmpty || editor.getText().trim() === '',
    }),
    [editor],
  );

  return (
    <div className={clsx(controlThemeStyle, focusRingStyle)} data-focused={isFocused || undefined} data-invalid={isInvalid || undefined}>
      <div className={containerStyle}>
        <Toolbar editor={editor} shortcodes={shortcodes} withLinkButton={withLinkButton} />

        <EditorContent editor={editor} />

        {withLinkButton && <LinkPopover editor={editor} />}
      </div>
    </div>
  );
};
